import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LayoutDashboard, FileText, Megaphone, Link2,
  Upload, Trash2, ExternalLink, LogOut, User, CheckCircle2, X,
  CreditCard, ShieldCheck, Settings, ClipboardList, UserPlus, Users,
  Facebook, Instagram, Linkedin, Twitter, Globe, Save, AlertTriangle, Calendar,
  TrendingUp, DollarSign, PieChart, Plus, Edit2, Phone, Mail, Tag,
  Building2, Briefcase, Receipt, BookOpen, FolderLock, Award, Star, Zap, Target, Play
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Toaster, toast } from 'sonner';
import { motion } from 'framer-motion';
import OnboardingFlow from '../components/OnboardingFlow';
import ReferralsTab from '../components/ReferralsTab';
import DocumentWorkflow from '../components/DocumentWorkflow';
import NotificationBell from '../components/NotificationBell';
import TeamManagement from '../components/TeamManagement';
import PipelineTab from '../components/PipelineTab';
import ContactsTab from '../components/ContactsTab';
import ExpensesTab from '../components/ExpensesTab';
import ListingsTab from '../components/ListingsTab';
import DealRoomsTab from '../components/DealRoomsTab';
import DownlineTab from '../components/DownlineTab';
import WebsiteBuilder from '../components/WebsiteBuilder';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'pipeline', label: 'Deals', icon: TrendingUp },
  { id: 'onboarding', label: 'Onboarding', icon: ClipboardList },
  { id: 'downline', label: 'My Network', icon: Users },
  { id: 'website', label: 'My Website', icon: Globe },
  { id: 'contacts', label: 'CRM', icon: Users },
  { id: 'listings', label: 'Listings', icon: Building2 },
  { id: 'expenses', label: 'Expenses', icon: Receipt },
  { id: 'deal-rooms', label: 'Deal Rooms', icon: FolderLock },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'campaigns', label: 'Marketing', icon: Megaphone },
  { id: 'team', label: 'My Team', icon: Users },
  { id: 'links', label: 'Tools & Links', icon: Link2 },
  { id: 'profile', label: 'Profile', icon: User },
];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [documents, setDocuments] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [myOptins, setMyOptins] = useState([]);
  const [links, setLinks] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profile, setProfile] = useState({ phone: '', license_number: '', license_state: '', specialization: '', preferred_location: '', bio: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [payingCampaign, setPayingCampaign] = useState(null);
  const [socialLinks, setSocialLinks] = useState({ facebook: '', instagram: '', linkedin: '', twitter: '', tiktok: '', youtube: '', website: '' });
  const [socialSaving, setSocialSaving] = useState(false);
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [milestones, setMilestones] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const fileInputRef = useRef(null);

  const fetchDocuments = useCallback(async () => {
    try { const res = await axios.get(`${API}/documents`, { withCredentials: true }); setDocuments(res.data); } catch { /* non-critical fetch */ }
  }, []);
  const fetchCampaigns = useCallback(async () => {
    try {
      const [camRes, optRes] = await Promise.all([axios.get(`${API}/marketing-campaigns`, { withCredentials: true }), axios.get(`${API}/marketing-campaigns/my-optins`, { withCredentials: true })]);
      setCampaigns(camRes.data); setMyOptins(optRes.data);
    } catch { /* non-critical fetch */ }
  }, []);
  const fetchLinks = useCallback(async () => {
    try { const res = await axios.get(`${API}/links`, { withCredentials: true }); setLinks(res.data); } catch { /* non-critical fetch */ }
  }, []);
  const fetchProfile = useCallback(async () => {
    try { const res = await axios.get(`${API}/profile`, { withCredentials: true }); setProfile(res.data); } catch { /* non-critical fetch */ }
  }, []);
  const fetchSocialLinks = useCallback(async () => {
    try { const res = await axios.get(`${API}/social-links`, { withCredentials: true }); setSocialLinks(res.data); } catch { /* non-critical fetch */ }
  }, []);
  const fetchLicenseStatus = useCallback(async () => {
    try { const res = await axios.get(`${API}/license-status`, { withCredentials: true }); setLicenseStatus(res.data); } catch { /* non-critical fetch */ }
  }, []);
  const fetchOnboardingStatus = useCallback(async () => {
    try { const res = await axios.get(`${API}/onboarding/status`, { withCredentials: true }); setOnboardingStatus(res.data); } catch { /* non-critical fetch */ }
  }, []);

  useEffect(() => {
    fetchDocuments(); fetchCampaigns(); fetchLinks(); fetchProfile(); fetchSocialLinks(); fetchLicenseStatus(); fetchOnboardingStatus();
    // Fetch milestones, forecast, and welcome status
    axios.get(`${API}/milestones`, { withCredentials: true }).then(r => setMilestones(r.data)).catch(() => {});
    axios.get(`${API}/pipeline/forecast`, { withCredentials: true }).then(r => setForecast(r.data)).catch(() => {});
    axios.get(`${API}/welcome-status`, { withCredentials: true }).then(r => { if (!r.data.seen) setShowWelcome(true); }).catch(() => {});
  }, [fetchDocuments, fetchCampaigns, fetchLinks, fetchProfile, fetchSocialLinks, fetchLicenseStatus, fetchOnboardingStatus]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData(); formData.append('file', file);
      await axios.post(`${API}/documents/upload?category=general`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded successfully'); fetchDocuments();
    } catch (err) { toast.error('Upload failed: ' + (err.response?.data?.detail || 'Unknown error')); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };
  const handleDelete = async (docId) => {
    try { await axios.delete(`${API}/documents/${docId}`, { withCredentials: true }); toast.success('Document deleted'); fetchDocuments(); } catch { toast.error('Delete failed'); }
  };
  const handlePayForCampaign = async (campaignId) => {
    setPayingCampaign(campaignId);
    try {
      const res = await axios.post(`${API}/payments/checkout`, { campaign_id: campaignId, origin_url: window.location.origin }, { withCredentials: true });
      window.location.href = res.data.url;
    } catch (err) { toast.error(err.response?.data?.detail || 'Payment initiation failed'); setPayingCampaign(null); }
  };
  const isOptedIn = (campaignId) => myOptins.some(o => o.campaign_id === campaignId);
  const handleSaveSocialLinks = async () => {
    setSocialSaving(true);
    try {
      const res = await axios.put(`${API}/social-links`, socialLinks, { withCredentials: true });
      setSocialLinks(res.data); toast.success('Social media links saved');
    } catch { toast.error('Failed to save social links'); } finally { setSocialSaving(false); }
  };
  const handleSaveProfile = async () => {
    setProfileSaving(true);
    try {
      const res = await axios.put(`${API}/profile`, { phone: profile.phone, license_number: profile.license_number, license_state: profile.license_state, specialization: profile.specialization, preferred_location: profile.preferred_location, bio: profile.bio }, { withCredentials: true });
      setProfile(res.data); toast.success('Profile updated');
    } catch { toast.error('Failed to save profile'); } finally { setProfileSaving(false); }
  };
  const handleLogout = async () => { await logout(); navigate('/'); };
  const isAdmin = user?.role === 'admin';
  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
  const dismissWelcome = async () => {
    setShowWelcome(false);
    try { await axios.post(`${API}/welcome-status/dismiss`, {}, { withCredentials: true }); } catch {}
  };

  return (
    <div className="min-h-screen bg-[#FAFBFC] flex" data-testid="agent-dashboard">
      <Toaster position="top-right" />

      {/* Welcome Video Modal */}
      {showWelcome && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="welcome-modal">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
            <div className="bg-gradient-to-br from-[#0F1729] to-[#002FA7] p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                    <Play size={18} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-['Cabinet_Grotesk'] font-bold">Welcome to Krieger!</h2>
                    <p className="text-xs text-blue-200">Your quick-start walkthrough</p>
                  </div>
                </div>
                <button onClick={dismissWelcome} className="text-white/50 hover:text-white transition-colors" data-testid="dismiss-welcome"><X size={18} /></button>
              </div>
            </div>
            <div className="p-6">
              <div className="aspect-video bg-slate-100 rounded-xl flex items-center justify-center mb-4 overflow-hidden relative group">
                <iframe
                  src="https://www.youtube.com/embed/dQw4w9WgXcQ?rel=0"
                  title="Krieger Welcome Walkthrough"
                  className="w-full h-full rounded-xl"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-800">Getting Started Checklist</h3>
                {[
                  { icon: User, text: 'Complete your agent profile', tab: 'profile' },
                  { icon: FileText, text: 'Sign your ICA agreement', tab: 'onboarding' },
                  { icon: Upload, text: 'Upload license & documents', tab: 'documents' },
                  { icon: TrendingUp, text: 'Add your first deal to the pipeline', tab: 'pipeline' },
                ].map((step, i) => (
                  <button key={i} onClick={() => { dismissWelcome(); setActiveTab(step.tab); }}
                    className="w-full flex items-center gap-3 p-2.5 rounded-lg border border-slate-100 hover:border-[#002FA7]/30 hover:bg-[#EFF3FF]/30 transition-all text-left"
                    data-testid={`welcome-step-${i}`}>
                    <div className="w-6 h-6 rounded-full bg-[#EFF3FF] flex items-center justify-center shrink-0">
                      <step.icon size={12} className="text-[#002FA7]" />
                    </div>
                    <span className="text-xs text-slate-600">{step.text}</span>
                  </button>
                ))}
              </div>
              <button onClick={dismissWelcome} className="w-full mt-4 bg-[#002FA7] hover:bg-[#002075] text-white rounded-lg py-2.5 text-sm font-medium transition-colors" data-testid="welcome-get-started">
                Got It — Let's Go!
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Sidebar - Desktop */}
      <aside className="hidden md:flex flex-col w-64 border-r border-slate-200 bg-white fixed top-0 left-0 bottom-0 z-40" data-testid="dashboard-sidebar">
        <div className="h-16 flex items-center justify-between px-6 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <img src="/krieger-logo.jpg" alt="Krieger" className="h-7 w-7 object-contain" />
            <span className="font-['Cabinet_Grotesk'] text-lg font-bold tracking-[0.15em] text-slate-900">KRIEGER</span>
          </div>
          <NotificationBell onNavigate={setActiveTab} />
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`sidebar-link w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg ${activeTab === tab.id ? 'active text-[#002FA7] font-medium' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid={`sidebar-${tab.id}`}>
              <tab.icon size={16} /> {tab.label}
            </button>
          ))}
          {isAdmin && (
            <button onClick={() => navigate('/admin')} className="sidebar-link w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg text-slate-500 hover:text-slate-700" data-testid="sidebar-admin">
              <ShieldCheck size={16} /> Admin Panel
            </button>
          )}
        </nav>
        <div className="border-t border-slate-100 p-4">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? <img src={user.picture} alt="" className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center"><User size={14} className="text-slate-400" /></div>}
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-800 truncate">{user?.name}</div>
              <div className="text-[10px] text-slate-400 truncate">{user?.email}</div>
            </div>
          </div>
          <button onClick={handleLogout} className="w-full flex items-center gap-2 text-sm text-slate-400 hover:text-slate-700 px-1 py-1.5 transition-colors" data-testid="sidebar-logout">
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile Bottom Nav */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-md border-b border-slate-200 h-14 flex items-center justify-between px-4" data-testid="mobile-header">
        <div className="flex items-center gap-2">
          <img src="/krieger-logo.jpg" alt="Krieger" className="h-6 w-6 object-contain" />
          <span className="font-['Cabinet_Grotesk'] text-lg font-bold tracking-[0.15em] text-slate-900">KRIEGER</span>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell onNavigate={(tab) => { setActiveTab(tab); }} />
          {user?.picture ? <img src={user.picture} alt="" className="w-7 h-7 rounded-full" /> : <div className="w-7 h-7 bg-slate-100 rounded-full" />}
        </div>
      </div>
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200 safe-area-bottom" data-testid="mobile-bottom-nav">
        <div className="flex items-center justify-around h-16 px-1">
          {TABS.slice(0, 5).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center justify-center gap-0.5 py-1 px-2 rounded-lg transition-colors min-w-0
                ${activeTab === tab.id ? 'text-[#002FA7]' : 'text-slate-400'}`}
              data-testid={`mobile-tab-${tab.id}`}
            >
              <tab.icon size={18} />
              <span className="text-[9px] font-medium truncate">{tab.label}</span>
            </button>
          ))}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`flex flex-col items-center justify-center gap-0.5 py-1 px-2 rounded-lg transition-colors
              ${mobileMenuOpen ? 'text-[#002FA7]' : 'text-slate-400'}`}
            data-testid="mobile-more-btn"
          >
            <Settings size={18} />
            <span className="text-[9px] font-medium">More</span>
          </button>
        </div>
      </nav>

      {/* Mobile "More" overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-black/30 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)}>
          <div className="absolute bottom-16 left-0 right-0 bg-white border-t border-slate-200 rounded-t-2xl p-4 space-y-1" onClick={(e) => e.stopPropagation()}>
            {TABS.slice(5).map(tab => (
              <button key={tab.id} onClick={() => { setActiveTab(tab.id); setMobileMenuOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm rounded-lg ${activeTab === tab.id ? 'bg-[#EFF3FF] text-[#002FA7] font-medium' : 'text-slate-500'}`}
                data-testid={`mobile-more-${tab.id}`}>
                <tab.icon size={16} /> {tab.label}
              </button>
            ))}
            {isAdmin && (
              <button onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 text-sm rounded-lg text-slate-500">
                <ShieldCheck size={16} /> Admin Panel
              </button>
            )}
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 text-sm rounded-lg text-red-400 hover:text-red-600">
              <LogOut size={16} /> Sign Out
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 md:ml-64 pt-14 md:pt-0 pb-20 md:pb-0">
        <div className="p-6 md:p-10 max-w-5xl">

          {/* OVERVIEW */}
          {activeTab === 'overview' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="tab-overview">
              <h1 className="text-3xl font-['Cabinet_Grotesk'] font-bold mb-2 text-slate-900">Welcome back, {user?.name?.split(' ')[0]}</h1>
              <p className="text-slate-500 mb-6">Your Krieger Real Estate agent portal.</p>

              {/* License Renewal Warning */}
              {licenseStatus?.status === 'expired' && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="license-expired-banner">
                  <AlertTriangle size={18} className="text-red-500 shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-red-800">License Expired</div>
                    <p className="text-xs text-red-600 mt-0.5">Your real estate license has expired. Please renew immediately and upload your updated license.</p>
                    <button onClick={() => setActiveTab('profile')} className="text-xs text-red-700 font-medium underline underline-offset-2 mt-1">Update License</button>
                  </div>
                </div>
              )}
              {licenseStatus?.status === 'critical' && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="license-critical-banner">
                  <AlertTriangle size={18} className="text-red-500 shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-red-800">License Expiring in {licenseStatus.days_until_expiry} Day{licenseStatus.days_until_expiry !== 1 ? 's' : ''}</div>
                    <p className="text-xs text-red-600 mt-0.5">Your license expires on {new Date(licenseStatus.expiry_date).toLocaleDateString()}. Renew now to avoid disruption.</p>
                    <button onClick={() => setActiveTab('profile')} className="text-xs text-red-700 font-medium underline underline-offset-2 mt-1">Update License</button>
                  </div>
                </div>
              )}
              {licenseStatus?.status === 'warning' && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="license-warning-banner">
                  <AlertTriangle size={18} className="text-amber-500 shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-amber-800">License Renewal Reminder</div>
                    <p className="text-xs text-amber-600 mt-0.5">Your license expires in {licenseStatus.days_until_expiry} days ({new Date(licenseStatus.expiry_date).toLocaleDateString()}). Plan your renewal ahead of time.</p>
                    <button onClick={() => setActiveTab('profile')} className="text-xs text-amber-700 font-medium underline underline-offset-2 mt-1">View License Details</button>
                  </div>
                </div>
              )}
              {licenseStatus?.status === 'not_set' && (
                <div className="bg-[#EFF3FF] border border-[#002FA7]/10 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="license-notset-banner">
                  <Calendar size={18} className="text-[#002FA7] shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-slate-700">Set Your License Expiry Date</div>
                    <p className="text-xs text-slate-500 mt-0.5">Add your license expiration date in your profile so we can send you timely renewal reminders.</p>
                    <button onClick={() => setActiveTab('profile')} className="text-xs text-[#002FA7] font-medium underline underline-offset-2 mt-1">Go to Profile</button>
                  </div>
                </div>
              )}

              {/* Onboarding Progress Bar */}
              {onboardingStatus && !onboardingStatus.orientation_complete && (
                <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6" data-testid="onboarding-progress">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-800">Onboarding Progress</h3>
                      <p className="text-xs text-slate-400 mt-0.5">Complete all steps to start closing deals.</p>
                    </div>
                    <span className="text-lg font-['Cabinet_Grotesk'] font-bold text-[#002FA7]">
                      {Math.round(([onboardingStatus.profile_complete, onboardingStatus.ica_signed, onboardingStatus.documents_uploaded, onboardingStatus.orientation_complete].filter(Boolean).length / 4) * 100)}%
                    </span>
                  </div>
                  {/* Progress bar */}
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden mb-4">
                    <div
                      className="h-full bg-[#002FA7] rounded-full transition-all duration-700"
                      style={{ width: `${([onboardingStatus.profile_complete, onboardingStatus.ica_signed, onboardingStatus.documents_uploaded, onboardingStatus.orientation_complete].filter(Boolean).length / 4) * 100}%` }}
                    />
                  </div>
                  {/* Steps */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {[
                      { key: 'profile_complete', label: 'Profile', icon: User, tab: 'onboarding' },
                      { key: 'ica_signed', label: 'Sign ICA', icon: FileText, tab: 'onboarding' },
                      { key: 'documents_uploaded', label: 'Documents', icon: Upload, tab: 'documents' },
                      { key: 'orientation_complete', label: 'Orientation', icon: CheckCircle2, tab: 'onboarding' },
                    ].map((step, i) => {
                      const done = onboardingStatus[step.key];
                      return (
                        <button
                          key={step.key}
                          onClick={() => setActiveTab(step.tab)}
                          className={`flex items-center gap-2 p-2.5 rounded-lg border transition-all text-left ${done ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200 hover:border-[#002FA7]/30'}`}
                          data-testid={`onboarding-step-${step.key}`}
                        >
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${done ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-400'}`}>
                            {done ? <CheckCircle2 size={12} /> : <span className="text-[10px] font-bold">{i + 1}</span>}
                          </div>
                          <span className={`text-xs font-medium ${done ? 'text-emerald-700' : 'text-slate-600'}`}>{step.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
                <div className="bento-card p-6 rounded-xl"><div className="text-[10px] tracking-[0.2em] uppercase text-slate-400 font-semibold mb-2">Your Commission</div><div className="commission-highlight text-4xl">90%</div><div className="text-xs text-slate-400 mt-1">Flat split on all transactions</div></div>
                <div className="bento-card p-6 rounded-xl"><div className="text-[10px] tracking-[0.2em] uppercase text-slate-400 font-semibold mb-2">Documents</div><div className="text-4xl font-['Cabinet_Grotesk'] font-bold text-slate-900">{documents.length}</div><div className="text-xs text-slate-400 mt-1">Uploaded files</div></div>
                <div className="bento-card p-6 rounded-xl"><div className="text-[10px] tracking-[0.2em] uppercase text-slate-400 font-semibold mb-2">Active Campaigns</div><div className="text-4xl font-['Cabinet_Grotesk'] font-bold text-slate-900">{myOptins.length}</div><div className="text-xs text-slate-400 mt-1">Marketing opt-ins</div></div>
              </div>

              {/* Milestone Badges */}
              {milestones && (milestones.earned.length > 0 || milestones.upcoming) && (
                <div className="bento-card p-6 rounded-xl mb-6" data-testid="milestones-panel">
                  <div className="flex items-center gap-2 mb-4">
                    <Award size={16} className="text-[#002FA7]" />
                    <h3 className="text-lg font-['Cabinet_Grotesk'] font-medium text-slate-800">Deal Milestones</h3>
                    <span className="text-[10px] text-slate-400 ml-auto">{milestones.total_deals} deals · {fmt(milestones.total_volume)}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {milestones.earned.map(m => (
                      <div key={m.id} className="flex items-center gap-2 px-3 py-2 rounded-xl border-2 bg-white" style={{ borderColor: m.color + '40' }} data-testid={`milestone-${m.id}`}>
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: m.color + '15' }}>
                          {m.icon === 'star' && <Star size={14} style={{ color: m.color }} />}
                          {m.icon === 'trending-up' && <TrendingUp size={14} style={{ color: m.color }} />}
                          {m.icon === 'award' && <Award size={14} style={{ color: m.color }} />}
                          {m.icon === 'zap' && <Zap size={14} style={{ color: m.color }} />}
                          {m.icon === 'crown' && <Award size={14} style={{ color: m.color }} />}
                          {m.icon === 'trophy' && <Award size={14} style={{ color: m.color }} />}
                          {m.icon === 'diamond' && <Star size={14} style={{ color: m.color }} />}
                        </div>
                        <div>
                          <div className="text-xs font-semibold text-slate-800">{m.label}</div>
                          <div className="text-[9px] text-slate-400">{m.description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {milestones.upcoming && (
                    <div className="bg-slate-50 rounded-xl p-3 flex items-center gap-3" data-testid="next-milestone">
                      <div className="w-8 h-8 rounded-lg bg-slate-200 flex items-center justify-center shrink-0">
                        <Target size={14} className="text-slate-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-slate-600">Next: {milestones.upcoming.label}</div>
                        <div className="text-[9px] text-slate-400">{milestones.upcoming.description}</div>
                      </div>
                      <div className="shrink-0 w-16">
                        <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
                          <div className="h-full bg-[#002FA7] rounded-full transition-all" style={{ width: `${milestones.upcoming.progress}%` }} />
                        </div>
                        <div className="text-[9px] text-slate-400 text-right mt-0.5">{Math.round(milestones.upcoming.progress)}%</div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Commission Forecast */}
              {forecast && (forecast.deals_in_pipeline > 0 || forecast.realized_this_quarter > 0) && (
                <div className="bento-card p-6 rounded-xl mb-6" data-testid="commission-forecast">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp size={16} className="text-[#002FA7]" />
                    <h3 className="text-lg font-['Cabinet_Grotesk'] font-medium text-slate-800">Quarterly Forecast</h3>
                    <span className="text-[10px] text-slate-400 ml-auto">{forecast.months_remaining} month{forecast.months_remaining !== 1 ? 's' : ''} remaining</span>
                  </div>

                  {/* Forecast summary */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
                      <div className="text-lg font-['Cabinet_Grotesk'] font-bold text-emerald-700">{fmt(forecast.realized_this_quarter)}</div>
                      <div className="text-[9px] text-emerald-500 uppercase tracking-wider font-semibold">Realized</div>
                    </div>
                    <div className="bg-[#EFF3FF] border border-[#002FA7]/10 rounded-xl p-3 text-center">
                      <div className="text-lg font-['Cabinet_Grotesk'] font-bold text-[#002FA7]">{fmt(forecast.projected_from_pipeline)}</div>
                      <div className="text-[9px] text-[#002FA7]/60 uppercase tracking-wider font-semibold">Projected</div>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
                      <div className="text-lg font-['Cabinet_Grotesk'] font-bold text-slate-800">{fmt(forecast.total_forecast)}</div>
                      <div className="text-[9px] text-slate-400 uppercase tracking-wider font-semibold">Total Forecast</div>
                    </div>
                  </div>

                  {/* Pipeline by stage */}
                  {forecast.by_stage.length > 0 && (
                    <div className="space-y-2">
                      <div className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">Pipeline Breakdown</div>
                      {forecast.by_stage.map(s => (
                        <div key={s.stage} className="flex items-center gap-3">
                          <div className="w-24 text-right text-[10px] text-slate-500 capitalize shrink-0">{s.stage.replace('_', ' ')}</div>
                          <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden relative">
                            <div className="h-full bg-[#002FA7]/20 rounded-full transition-all" style={{ width: `${forecast.projected_from_pipeline > 0 ? (s.weighted_commission / forecast.projected_from_pipeline) * 100 : 0}%` }}>
                            </div>
                            <span className="absolute inset-0 flex items-center justify-center text-[9px] font-medium text-slate-600">{fmt(s.weighted_commission)} ({Math.round(s.probability * 100)}%)</span>
                          </div>
                          <div className="w-12 text-right text-[10px] text-slate-400 shrink-0">{s.count} deal{s.count !== 1 ? 's' : ''}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="bento-card p-6 rounded-xl">
                <h3 className="text-lg font-['Cabinet_Grotesk'] font-medium mb-4 text-slate-800">Quick Actions</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[{ tab: 'documents', icon: FileText, label: 'Manage Documents' }, { tab: 'campaigns', icon: Megaphone, label: 'Marketing Campaigns' }, { tab: 'links', icon: Link2, label: 'Tools & Software' }, { tab: 'profile', icon: Settings, label: 'Edit Profile' }].map(q => (
                    <button key={q.tab} onClick={() => setActiveTab(q.tab)} className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors text-left" data-testid={`quick-${q.tab}`}>
                      <q.icon size={18} className="text-[#002FA7]" /><span className="text-sm text-slate-600">{q.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* ONBOARDING */}
          {activeTab === 'onboarding' && (
            <OnboardingFlow onComplete={() => setActiveTab('overview')} />
          )}

          {/* DOWNLINE / NETWORK */}
          {activeTab === 'downline' && <DownlineTab />}

          {/* MY WEBSITE */}
          {activeTab === 'website' && <WebsiteBuilder />}

          {/* DEAL PIPELINE */}
          {activeTab === 'pipeline' && <PipelineTab />}

          {/* CRM / CONTACTS */}
          {activeTab === 'contacts' && <ContactsTab />}

          {/* LISTINGS */}
          {activeTab === 'listings' && <ListingsTab />}

          {/* EXPENSES */}
          {activeTab === 'expenses' && <ExpensesTab />}

          {/* DEAL ROOMS */}
          {activeTab === 'deal-rooms' && <DealRoomsTab />}

          {/* DOCUMENTS */}
          {activeTab === 'documents' && (
            <DocumentWorkflow onAllUploaded={() => {}} />
          )}

          {/* CAMPAIGNS with Stripe */}
          {activeTab === 'campaigns' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="tab-campaigns">
              <h1 className="text-3xl font-['Cabinet_Grotesk'] font-bold mb-1 text-slate-900">Marketing</h1>
              <p className="text-sm text-slate-500 mb-8">Manage your social presence and subscribe to campaigns.</p>

              {/* Social Media Links Section */}
              <div className="bento-card p-6 rounded-xl mb-8" data-testid="social-links-section">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-lg bg-[#EFF3FF] flex items-center justify-center"><Globe size={18} className="text-[#002FA7]" /></div>
                  <div>
                    <h3 className="text-base font-['Cabinet_Grotesk'] font-medium text-slate-800">Your Social Media</h3>
                    <p className="text-xs text-slate-400">Add your profiles so clients and the brokerage can find you online.</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { key: 'facebook', label: 'Facebook', icon: Facebook, placeholder: 'https://facebook.com/yourpage' },
                    { key: 'instagram', label: 'Instagram', icon: Instagram, placeholder: 'https://instagram.com/yourhandle' },
                    { key: 'linkedin', label: 'LinkedIn', icon: Linkedin, placeholder: 'https://linkedin.com/in/yourprofile' },
                    { key: 'twitter', label: 'X (Twitter)', icon: Twitter, placeholder: 'https://x.com/yourhandle' },
                    { key: 'tiktok', label: 'TikTok', icon: () => <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.73a8.19 8.19 0 004.76 1.52V6.8a4.84 4.84 0 01-1-.11z"/></svg>, placeholder: 'https://tiktok.com/@yourhandle' },
                    { key: 'youtube', label: 'YouTube', icon: () => <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M23.5 6.19a3 3 0 00-2.12-2.12C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.38.57A3 3 0 00.5 6.19 31.25 31.25 0 000 12a31.25 31.25 0 00.5 5.81 3 3 0 002.12 2.12c1.88.57 9.38.57 9.38.57s7.5 0 9.38-.57a3 3 0 002.12-2.12A31.25 31.25 0 0024 12a31.25 31.25 0 00-.5-5.81zM9.75 15.02V8.98L15.5 12l-5.75 3.02z"/></svg>, placeholder: 'https://youtube.com/@yourchannel' },
                    { key: 'website', label: 'Personal Website', icon: Globe, placeholder: 'https://yourwebsite.com' },
                  ].map(({ key, label, icon: Icon, placeholder }) => (
                    <div key={key}>
                      <Label className="text-xs text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                        <Icon size={12} /> {label}
                      </Label>
                      <Input
                        value={socialLinks[key] || ''}
                        onChange={(e) => setSocialLinks(s => ({ ...s, [key]: e.target.value }))}
                        className="bg-white border-slate-200 text-slate-800 rounded-lg text-sm"
                        placeholder={placeholder}
                        data-testid={`social-${key}`}
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-5 flex items-center gap-3">
                  <Button onClick={handleSaveSocialLinks} disabled={socialSaving} className="bg-[#002FA7] text-white hover:bg-[#0040D6] rounded-full text-xs" data-testid="save-social-links-btn">
                    <Save size={12} className="mr-1.5" />{socialSaving ? 'Saving...' : 'Save Social Links'}
                  </Button>
                  {Object.values(socialLinks).some(v => v) && (
                    <span className="text-xs text-slate-400">{Object.values(socialLinks).filter(v => v).length} profile(s) linked</span>
                  )}
                </div>
              </div>

              {/* Campaigns Section */}
              <div className="flex items-center gap-3 mb-5">
                <div className="w-9 h-9 rounded-lg bg-[#EFF3FF] flex items-center justify-center"><Megaphone size={18} className="text-[#002FA7]" /></div>
                <div>
                  <h3 className="text-base font-['Cabinet_Grotesk'] font-medium text-slate-800">Marketing Campaigns</h3>
                  <p className="text-xs text-slate-400">Subscribe to campaigns to grow your business. Pay securely via Stripe.</p>
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{campaigns.map(camp => {
                const opted = isOptedIn(camp.id); const paying = payingCampaign === camp.id;
                const priceLabel = camp.price_note || `$${camp.price}/mo`;
                return (
                  <div key={camp.id} className="bento-card p-6 rounded-xl" data-testid={`campaign-${camp.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div><Badge variant="outline" className="text-[10px] border-slate-200 text-slate-400 mb-2">{camp.category}</Badge><h3 className="text-base font-['Cabinet_Grotesk'] font-medium text-slate-800">{camp.name}</h3></div>
                      <div className="text-right"><div className="text-lg font-['Cabinet_Grotesk'] font-bold text-slate-900">${camp.price}</div>{camp.price_note && <div className="text-[10px] text-slate-400 mt-0.5 max-w-[140px] leading-tight">{camp.price_note}</div>}</div>
                    </div>
                    <p className="text-sm text-slate-400 mb-4 leading-relaxed">{camp.description}</p>
                    <ul className="space-y-1.5 mb-5">{camp.features.map((f, i) => (<li key={i} className="flex items-center gap-2 text-xs text-slate-400"><CheckCircle2 size={12} className="text-slate-300 shrink-0" /> {f}</li>))}</ul>
                    {opted ? (
                      <Badge className="bg-[#EFF3FF] text-[#002FA7] border-[#002FA7]/20"><CheckCircle2 size={12} className="mr-1" /> Active</Badge>
                    ) : (
                      <Button onClick={() => handlePayForCampaign(camp.id)} disabled={paying} size="sm" className="bg-[#002FA7] text-white hover:bg-[#0040D6] rounded-full text-xs" data-testid={`pay-${camp.id}`}>
                        <CreditCard size={12} className="mr-1" />{paying ? 'Redirecting...' : `Subscribe — ${priceLabel}`}
                      </Button>
                    )}
                  </div>
                );
              })}</div>
            </motion.div>
          )}

          {/* MY TEAM */}
          {activeTab === 'team' && <TeamManagement />}

          {/* LINKS */}
          {activeTab === 'links' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="tab-links">
              <h1 className="text-3xl font-['Cabinet_Grotesk'] font-bold mb-1 text-slate-900">Tools & Software</h1>
              <p className="text-sm text-slate-500 mb-8">Quick access to your essential real estate tools.</p>
              {!links.some(l => l.id.startsWith('link_mls')) && (
                <div className="bento-card p-4 rounded-xl mb-6 border-l-4 border-[#002FA7] bg-[#F0F4FF]" data-testid="mls-state-notice">
                  <p className="text-sm text-slate-600">Set your <button onClick={() => setActiveTab('profile')} className="text-[#002FA7] font-medium underline underline-offset-2">License State</button> in your profile to see your region's MLS portal.</p>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{links.map(link => (
                <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer" className="bento-card p-6 rounded-xl group flex flex-col" data-testid={`link-${link.id}`}>
                  <div className="flex items-center justify-between mb-3"><h3 className="text-base font-['Cabinet_Grotesk'] font-medium text-slate-800">{link.name}</h3><ExternalLink size={14} className="text-slate-300 group-hover:text-[#002FA7] transition-colors" /></div>
                  <p className="text-sm text-slate-400 leading-relaxed">{link.description}</p>
                </a>
              ))}</div>
            </motion.div>
          )}

          {/* PROFILE */}
          {activeTab === 'profile' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="tab-profile">
              <h1 className="text-3xl font-['Cabinet_Grotesk'] font-bold mb-1 text-slate-900">Your Profile</h1>
              <p className="text-sm text-slate-500 mb-8">Update your agent information.</p>
              <div className="bento-card p-6 rounded-xl mb-6">
                <div className="flex items-center gap-4 mb-6">
                  {user?.picture ? <img src={user.picture} alt="" className="w-16 h-16 rounded-full" /> : <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center"><User size={24} className="text-slate-400" /></div>}
                  <div><div className="text-lg font-['Cabinet_Grotesk'] font-medium text-slate-900">{user?.name}</div><div className="text-sm text-slate-400">{user?.email}</div><Badge variant="outline" className="text-[9px] border-slate-200 text-slate-400 mt-1">{user?.role || 'agent'}</Badge></div>
                </div>
              </div>
              <div className="bento-card p-6 rounded-xl space-y-5">
                <h3 className="text-base font-['Cabinet_Grotesk'] font-medium mb-4 text-slate-800">Contact & License</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label className="text-xs text-slate-400 uppercase tracking-wider mb-2 block">Phone</Label><Input value={profile.phone || ''} onChange={(e) => setProfile(p => ({ ...p, phone: e.target.value }))} className="bg-white border-slate-200 text-slate-800 rounded-lg" placeholder="(555) 123-4567" data-testid="profile-phone" /></div>
                  <div><Label className="text-xs text-slate-400 uppercase tracking-wider mb-2 block">License Number</Label><Input value={profile.license_number || ''} onChange={(e) => setProfile(p => ({ ...p, license_number: e.target.value }))} className="bg-white border-slate-200 text-slate-800 rounded-lg" placeholder="RE-12345" data-testid="profile-license" /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label className="text-xs text-slate-400 uppercase tracking-wider mb-2 block">License State</Label>
                    <Select value={profile.license_state || ''} onValueChange={(v) => setProfile(p => ({ ...p, license_state: v }))}>
                      <SelectTrigger className="bg-white border-slate-200 text-slate-800 rounded-lg" data-testid="profile-license-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                      <SelectContent className="bg-white border-slate-200"><SelectItem value="NY">New York</SelectItem><SelectItem value="PA">Pennsylvania</SelectItem><SelectItem value="FL">Florida</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs text-slate-400 uppercase tracking-wider mb-2 block">License Expiry Date</Label>
                    <Input type="date" value={profile.license_expiry_date || ''} onChange={(e) => setProfile(p => ({ ...p, license_expiry_date: e.target.value }))}
                      className="bg-white border-slate-200 text-slate-800 rounded-lg" data-testid="profile-license-expiry" />
                    {licenseStatus?.status === 'expired' && <span className="text-[10px] text-red-500 font-medium mt-1 block">Expired — renew immediately</span>}
                    {licenseStatus?.status === 'critical' && <span className="text-[10px] text-red-500 font-medium mt-1 block">Expires in {licenseStatus.days_until_expiry} days</span>}
                    {licenseStatus?.status === 'warning' && <span className="text-[10px] text-amber-500 font-medium mt-1 block">Expires in {licenseStatus.days_until_expiry} days</span>}
                    {licenseStatus?.status === 'active' && <span className="text-[10px] text-emerald-500 font-medium mt-1 block">Active — {licenseStatus.days_until_expiry} days remaining</span>}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label className="text-xs text-slate-400 uppercase tracking-wider mb-2 block">Specialization</Label>
                    <Select value={profile.specialization || ''} onValueChange={(v) => setProfile(p => ({ ...p, specialization: v }))}>
                      <SelectTrigger className="bg-white border-slate-200 text-slate-800 rounded-lg" data-testid="profile-specialization"><SelectValue placeholder="Select focus" /></SelectTrigger>
                      <SelectContent className="bg-white border-slate-200"><SelectItem value="multifamily">Multifamily</SelectItem><SelectItem value="office">Office Buildings</SelectItem><SelectItem value="retail_restaurant">Retail / Restaurant</SelectItem><SelectItem value="self_storage">Self Storage</SelectItem><SelectItem value="warehouse_industrial">Warehouse / Industrial</SelectItem><SelectItem value="mixed_commercial">Mixed Commercial</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label className="text-xs text-slate-400 uppercase tracking-wider mb-2 block">Preferred Location</Label>
                  <Select value={profile.preferred_location || ''} onValueChange={(v) => setProfile(p => ({ ...p, preferred_location: v }))}>
                    <SelectTrigger className="bg-white border-slate-200 text-slate-800 rounded-lg" data-testid="profile-location"><SelectValue placeholder="Where do you operate?" /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200"><SelectItem value="New York">New York</SelectItem><SelectItem value="Pennsylvania">Pennsylvania</SelectItem><SelectItem value="Florida">Florida</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs text-slate-400 uppercase tracking-wider mb-2 block">Bio</Label><Textarea value={profile.bio || ''} onChange={(e) => setProfile(p => ({ ...p, bio: e.target.value }))} className="bg-white border-slate-200 text-slate-800 rounded-lg min-h-[100px]" placeholder="Tell us about yourself..." data-testid="profile-bio" /></div>
                <Button onClick={handleSaveProfile} disabled={profileSaving} className="bg-[#002FA7] text-white hover:bg-[#0040D6] rounded-full" data-testid="save-profile-btn">{profileSaving ? 'Saving...' : 'Save Profile'}</Button>
              </div>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
}
